import { createSignal, For, Show } from 'solid-js'
import Button from '~/components/Button'
import Icon from '~/components/Icon'
import ListItemButton from '~/components/ListItemButton'
import type { WorkflowExecutionDetailType, WorkflowExecutionType, WorkflowStatsType } from '~/types/workflow'
import { executionColor, executionIcon, executionLabel, formatDuration, relativeTime } from './workflowMeta'

export interface ExecutionPanelPropsType {
  executions: WorkflowExecutionType[]
  stats: WorkflowStatsType | null
  loading: boolean
  onRefresh: () => void
  onOpen: (publicId: string) => void
}

/**
 * Listado de ejecuciones con métricas resumidas. Usado dentro del drawer de
 * historial del editor. Cada ejecución es un `ListItemButton` de la librería.
 */
export function ExecutionPanel(props: ExecutionPanelPropsType) {
  const total = () => props.stats?.byStatus?.reduce((acc, r) => acc + Number(r.c), 0) ?? 0
  const ok = () => Number(props.stats?.byStatus?.find(r => r.status === 'succeeded')?.c ?? 0)
  const rate = () => (total() > 0 ? Math.round((ok() / total()) * 100) : 0)

  return (
    <div class="space-y-3">
      <Show when={props.stats}>
        <div class="rounded-lg border border-border bg-surface-container/50 p-3 text-xs">
          <div class="flex items-center justify-between">
            <span class="text-foreground-subtle">Ejecuciones (últimos {props.stats?.periodDays ?? 0} d)</span>
            <span class={`font-semibold ${rate() >= 70 ? 'text-success' : rate() > 0 ? 'text-warning' : 'text-foreground-subtle'}`}>{rate()}% éxito</span>
          </div>
          <div class="mt-2 flex items-center gap-1">
            <div class="h-1.5 flex-1 overflow-hidden rounded-full bg-surface-muted">
              <div class="h-full rounded-full bg-success" style={{ width: `${rate()}%` }} />
            </div>
          </div>
          <Show when={props.stats?.retries?.total_retries}>
            <p class="mt-2 text-foreground-subtle">
              Reintentos: <b class="text-foreground">{props.stats?.retries?.total_retries}</b>
              <Show when={props.stats?.retries?.most_retried_node}>
                <span class="ml-2 truncate">· {props.stats?.retries?.most_retried_node}</span>
              </Show>
            </p>
          </Show>
        </div>
      </Show>

      <div class="space-y-1.5">
        <For each={props.executions}>
          {ex => (
            <ListItemButton class="!rounded-lg !p-3" onClick={() => props.onOpen(ex.publicId)}>
              <div class="flex w-full items-center gap-3">
                <span class={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full ${softBg(executionColor(ex.status))}`}>
                  <Icon icon={executionIcon(ex.status)} size={15} />
                </span>
                <div class="min-w-0 flex-1">
                  <div class="flex items-center justify-between gap-2">
                    <span class="truncate font-mono text-xs text-foreground">{ex.publicId}</span>
                    <span class={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-medium ${softBg(executionColor(ex.status))}`}>{executionLabel(ex.status)}</span>
                  </div>
                  <div class="mt-1 flex items-center justify-between gap-2 text-xs text-foreground-subtle">
                    <span>
                      {ex.triggerType} · {ex.mode === 'test' ? 'prueba' : 'producción'}
                    </span>
                    <span>{ex.durationMs != null ? formatDuration(ex.durationMs) : '—'}</span>
                  </div>
                  <p class="mt-0.5 text-[11px] text-foreground-subtle">{relativeTime(ex.createdAt)}</p>
                </div>
              </div>
            </ListItemButton>
          )}
        </For>
      </div>

      <Show when={!props.executions.length && !props.loading}>
        <div class="py-8 text-center">
          <Icon icon="material-symbols-light:hourglass-empty-rounded" size={32} class="mx-auto text-foreground-subtle" />
          <p class="mt-2 text-sm text-foreground-subtle">Todavía no hay ejecuciones.</p>
          <p class="text-xs text-foreground-subtle">Pulsa «Probar» para ejecutar el flujo en modo prueba.</p>
        </div>
      </Show>
    </div>
  )
}

function softBg(color: 'success' | 'danger' | 'info' | 'warning' | 'secondary'): string {
  const map: Record<string, string> = {
    success: 'bg-success-soft text-success',
    danger: 'bg-danger-soft text-danger',
    info: 'bg-info-soft text-info',
    warning: 'bg-warning-soft text-warning',
    secondary: 'bg-surface-muted text-foreground-subtle'
  }
  return map[color] ?? map.secondary
}

export interface ExecutionDetailPropsType {
  detail: WorkflowExecutionDetailType | null
  onClose: () => void
  onRetry: (publicId: string) => void
  onCancel: (publicId: string) => void
}

/**
 * Detalle de una ejecución: cabecera con estado/duración y línea de tiempo de
 * nodos. Los detalles técnicos (input/output/error) van en bloques plegables.
 */
export function ExecutionDetail(props: ExecutionDetailPropsType) {
  const [expanded, setExpanded] = createSignal<string | null>(null)
  const ex = () => props.detail?.execution

  return (
    <div class="space-y-3">
      <Show when={props.detail}>
        <div class="rounded-lg border border-border bg-surface p-3">
          <div class="flex items-center justify-between">
            <span class="font-mono text-xs">{ex()?.publicId}</span>
            <span class={`rounded-full px-2 py-0.5 text-[10px] font-medium ${softBg(executionColor(ex()?.status ?? 'queued'))}`}>{executionLabel(ex()?.status ?? 'queued')}</span>
          </div>
          <dl class="mt-2 grid grid-cols-2 gap-x-3 gap-y-1 text-xs text-foreground-subtle">
            <div>
              <dt>Iniciada</dt>
              <dd class="text-foreground-muted">{relativeTime(ex()?.startedAt ?? ex()?.createdAt)}</dd>
            </div>
            <div>
              <dt>Duración</dt>
              <dd class="text-foreground-muted">{ex()?.durationMs != null ? formatDuration(ex()?.durationMs) : '—'}</dd>
            </div>
            <div>
              <dt>Modo</dt>
              <dd class="text-foreground-muted">{ex()?.mode === 'test' ? 'Prueba' : 'Producción'}</dd>
            </div>
            <div>
              <dt>Disparador</dt>
              <dd class="text-foreground-muted">{ex()?.triggerType ?? '—'}</dd>
            </div>
          </dl>
          <Show when={ex()?.error}>
            <div class="mt-2 rounded-md bg-danger-soft px-2 py-1.5 text-xs text-danger">
              <p class="font-medium">{ex()?.error?.message ?? 'Error de ejecución'}</p>
              <p class="mt-0.5 font-mono text-[10px]">{ex()?.error?.code}</p>
            </div>
          </Show>
        </div>

        {/* Timeline de nodos */}
        <div class="space-y-1.5">
          <p class="text-xs font-semibold text-foreground">Timeline</p>
          <For each={props.detail?.nodes ?? []}>
            {n => {
              const isOpen = expanded() === n.nodeId
              return (
                <div
                  class={`rounded-lg border p-2.5 text-xs ${n.status === 'failed' ? 'border-danger/40 bg-danger-soft/10' : n.status === 'succeeded' ? 'border-success/40 bg-success-soft/10' : 'border-border bg-surface'}`}
                >
                  <ListItemButton class="!rounded-lg !p-0" selected={isOpen} onClick={() => setExpanded(isOpen ? null : n.nodeId)}>
                    <div class="flex w-full items-center gap-2 py-1">
                      <span class={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full ${softBg(executionColor(n.status))}`}>
                        <Icon icon={executionIcon(n.status)} size={13} />
                      </span>
                      <span class="min-w-0 flex-1 truncate font-medium">{n.nodeName ?? n.nodeType}</span>
                      <Show when={n.durationMs != null}>
                        <span class="shrink-0 font-mono text-[10px] text-foreground-subtle">{formatDuration(n.durationMs)}</span>
                      </Show>
                      <Show when={n.outputPort}>
                        <span class="shrink-0 rounded bg-surface-muted px-1.5 py-0.5 text-[10px]">→ {n.outputPort}</span>
                      </Show>
                      <Icon icon={isOpen ? 'material-symbols-light:expand-less' : 'material-symbols-light:expand-more'} size={16} class="shrink-0 text-foreground-subtle" />
                    </div>
                  </ListItemButton>
                  <Show when={isOpen}>
                    <div class="mt-2 space-y-1.5 border-t border-border pt-2">
                      <Show when={n.error}>
                        <DetailBlock title="Error" value={n.error} danger />
                      </Show>
                      <Show when={n.input !== undefined}>
                        <DetailBlock title="Entrada" value={n.input} />
                      </Show>
                      <Show when={n.output !== undefined}>
                        <DetailBlock title="Salida" value={n.output} />
                      </Show>
                    </div>
                  </Show>
                </div>
              )
            }}
          </For>
        </div>

        <div class="flex flex-wrap justify-end gap-2 pt-1">
          <Button size="sm" variant="soft" onClick={props.onClose}>
            Cerrar
          </Button>
          <Show when={ex()?.status === 'failed'}>
            <Button size="sm" color="warning" icon="material-symbols-light:refresh" onClick={() => props.onRetry(ex()!.publicId)}>
              Reintentar
            </Button>
          </Show>
          <Show when={ex() && ['queued', 'running', 'waiting'].includes(ex()!.status)}>
            <Button size="sm" color="danger" variant="soft" icon="material-symbols-light:stop-circle-outline" onClick={() => props.onCancel(ex()!.publicId)}>
              Cancelar
            </Button>
          </Show>
        </div>
      </Show>
    </div>
  )
}

function DetailBlock(props: { title: string; value: unknown; danger?: boolean }) {
  const text = () => (typeof props.value === 'string' ? props.value : JSON.stringify(props.value, null, 2))
  return (
    <div>
      <p class={`mb-0.5 text-[10px] font-semibold uppercase tracking-wide ${props.danger ? 'text-danger' : 'text-foreground-subtle'}`}>{props.title}</p>
      <pre class={`max-h-40 overflow-auto rounded-md p-2 text-[10px] leading-relaxed ${props.danger ? 'bg-danger-soft/60 text-danger' : 'bg-surface-muted text-foreground-muted'}`}>{text()}</pre>
    </div>
  )
}
